// Ana sayfa widget yerleşimi: `UserSettings["WidgetLayout"]` oku/yaz (Blazor HomePage ile ortak ayar).
// Değişiklikler 800 ms gecikmeyle kaydedilir (Blazor'daki debounce ile aynı).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { useAuthStore } from '@/entities/session/model/authStore';
import { WIDGET_LAYOUT_SETTING_KEY, defaultWidgets, parseWidgetLayout, serializeWidgetLayout, type WidgetDefinition } from '../model/widgetLayout';

interface UserSettingDto {
  userId: string;
  settingKey: string;
  settingValue: string | null;
}

const SAVE_DEBOUNCE_MS = 800;

export function useWidgetLayout() {
  const userId = useAuthStore((s) => s.user?.userId ?? '');
  const [widgets, setWidgets] = useState<WidgetDefinition[] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const query = useQuery({
    queryKey: ['home', 'widgetLayout', userId],
    queryFn: () => apiClient.get<UserSettingDto>(ENDPOINTS.USER_SETTING(userId, WIDGET_LAYOUT_SETTING_KEY), { octo: { expectSuccess: false } }),
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Sunucudan gelen ayar bir kez yerel duruma alınır; sonrasında yerel durum yönetir
  const loaded = query.isSuccess || query.isError;
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!loaded || hydratedRef.current) return;
    hydratedRef.current = true;
    setWidgets(query.data?.settingValue ? parseWidgetLayout(query.data.settingValue) : defaultWidgets());
  }, [loaded, query.data]);

  const persist = useCallback(
    (next: WidgetDefinition[]) => {
      if (!userId) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void apiClient
          .post(ENDPOINTS.USER_SETTINGS_SAVE, { userId, settingKey: WIDGET_LAYOUT_SETTING_KEY, settingValue: serializeWidgetLayout(next) }, { octo: { expectSuccess: false } })
          .catch(() => {
            /* ayar kaydedilemezse yerleşim yerel kalır */
          });
      }, SAVE_DEBOUNCE_MS);
    },
    [userId],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const update = useCallback(
    (next: WidgetDefinition[]) => {
      setWidgets(next);
      persist(next);
    },
    [persist],
  );

  const reset = useCallback(() => update(defaultWidgets()), [update]);

  return { widgets, isLoading: !loaded || widgets === null, update, reset };
}
